import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useSaveWishlistMutation, useUnSaveWishlistMutation } from '../../../../service';
import videoApi from '../../../../api/videoApi';

export const useVideo = (userToken, userId) => {
	const [savedVideoIds, setSavedVideoIds] = useState([]);
	const [loading, setLoading] = useState(false);

	// RTK Query hooks
	const [saveWishlist] = useSaveWishlistMutation();
	const [unsaveWishlist] = useUnSaveWishlistMutation();

	// Fetch saved video IDs
	const fetchSavedVideoIds = useCallback(async () => {
		if (!userId) return [];

		try {
			setLoading(true);
			const response = await videoApi.getAllSavedVideoIds(userId);
			setSavedVideoIds(response.data.savedVideos || []);
			return response.data.savedVideos || [];
		} catch (error) {
			console.error('Error fetching saved videos:', error);
			return [];
		} finally {
			setLoading(false);
		}
	}, [userId]);

	// Toggle save/unsave status
	const toggleSaveVideo = useCallback(
		async videoId => {
			if (!userToken || !videoId) return;

			try {
				setLoading(true);

				const isCurrentlySaved = savedVideoIds.includes(videoId);

				if (isCurrentlySaved) {
					await unsaveWishlist({ id: videoId, token: userToken }).unwrap();
					toast.success('Video removed from saved list', {
						position: 'top-center',
						duration: 3000
					});
					setSavedVideoIds(prev => prev.filter(id => id !== videoId));
				} else {
					await saveWishlist({ id: videoId, token: userToken }).unwrap();
					toast.success('Video saved successfully', {
						position: 'top-center',
						duration: 3000
					});
					setSavedVideoIds(prev => [...prev, videoId]);
				}

				return !isCurrentlySaved;
			} catch (error) {
				toast.error('Failed to update saved status', {
					position: 'top-center',
					duration: 3000
				});
				return null;
			} finally {
				setLoading(false);
			}
		},
		[userToken, savedVideoIds, saveWishlist, unsaveWishlist]
	);

	return {
		savedVideoIds,
		isVideoSaved: videoId => savedVideoIds.includes(videoId),
		isLoading: loading,
		fetchSavedVideoIds,
		toggleSaveVideo
	};
};
